// A fax that moves.
//
// The whole PAGE animates, not just the picture in the middle: every frame goes
// through `renderPage`, so each one gets the header line, the cover band, the
// reply URL and the artefacts. That is the difference between a fax of a moving
// thing and a moving thing with a fax border.
//
// THE SEED IS HELD STILL ON PURPOSE. `mulberry32(fax.seed)` drives the streaks,
// the dropped scanlines and the edge crud, and re-seeding per frame made them
// crawl — every artefact flickering in a different place twelve times a second,
// which reads as television static rather than as a dirty roller. A real
// machine's roller is dirty in the SAME place for the whole page, so the
// artefacts stay put and only the content moves.

import { renderPage } from './render.js';
import { GifWriter } from './gif.js';

/**
 * A CLIP is stored as its frames, not as the video.
 *
 * A phone video is tens of megabytes and the fax needs twelve pictures out of a
 * five-second window of it. So the trimmer extracts those twelve at grid width,
 * as JPEG data URLs, and `source` holds them as JSON: a clip fax is about the
 * size of twelve photo faxes, and it re-renders identically forever, which is
 * what lets the outbox keep a recipe rather than a file.
 */
export function packClip({ frames, delaysMs }) {
  return JSON.stringify({ v: 1, frames, delaysMs });
}
export async function bitmapsFromClip(source) {
  let parsed;
  try { parsed = JSON.parse(source); } catch { parsed = null; }
  const frames = Array.isArray(parsed?.frames) ? parsed.frames : [];
  if (!frames.length) throw Object.assign(new Error('That clip has no frames.'), { code: 'empty' });
  // Decoded from the bytes, never fetch()ed: a fetch of a data: URL is governed
  // by connect-src, and the website's CSP is 'self' only — the preview came
  // up as an unsized black canvas with "Failed to fetch" buried in the console.
  const bitmaps = await Promise.all(frames.map((u) => createImageBitmap(blobFromDataUrl(u))));
  const delaysMs = Array.isArray(parsed.delaysMs) && parsed.delaysMs.length === frames.length
    ? parsed.delaysMs : frames.map(() => 110);
  return { bitmaps, delaysMs };
}

/** The same bytes as a Blob with the data URL's own MIME type, for createImageBitmap. */
export function blobFromDataUrl(dataUrl) {
  const type = String(dataUrl).match(/^data:([^;,]+)/)?.[1] || "application/octet-stream";
  return new Blob([bytesFromDataUrl(dataUrl)], { type });
}

/** A stored GIF is a data URL, like a photo. The decoder wants its bytes back. */
export function bytesFromDataUrl(dataUrl) {
  const i = String(dataUrl).indexOf(',');
  const b64 = i === -1 ? '' : dataUrl.slice(i + 1);
  const bin = globalThis.atob(b64);
  const out = new Uint8Array(bin.length);
  for (let k = 0; k < bin.length; k++) out[k] = bin.charCodeAt(k);
  return out;
}

/** The page canvas, read back as one byte per pixel: 0 paper, 1 ink. */
function toIndices(canvas) {
  const g = canvas.getContext('2d');
  const { data, width, height } = g.getImageData(0, 0, canvas.width, canvas.height);
  const out = new Uint8Array(width * height);
  // The page is already pure black and white — `renderPage` thresholds it last,
  // precisely so nothing downstream has to guess. Mid-grey cannot occur, so the
  // comparison is a formality that also catches a future regression.
  for (let i = 0, p = 0; p < out.length; i += 4, p += 1) out[p] = data[i] < 128 ? 1 : 0;
  return { indices: out, width, height };
}

/**
 * @param {object} o
 * @param {ImageBitmap[]} o.bitmaps
 * @param {number[]} o.delaysMs
 * @param {object} o.fax
 * @param {object} [o.machine]
 * @param {string} [o.replyUrl]
 * @param {(n:number)=>void} [o.onProgress]
 * @returns {Promise<{blob: Blob, poster: any, frames: number}>}
 */
export async function renderAnimated({
  bitmaps, delaysMs, fax, machine = {}, replyUrl = '', onProgress, previewFrames = 0, previewWidth = 540,
}) {
  if (!bitmaps?.length) throw new Error('renderAnimated needs at least one frame');

  let writer = null;
  let poster = null;
  const previews = [];
  // Which frames also become a preview: evenly spaced, so a long clip still
  // shows its whole motion in a handful of pictures.
  const wantPreview = new Set();
  if (previewFrames > 0) {
    const step = bitmaps.length / Math.min(previewFrames, bitmaps.length);
    for (let i = 0; i < Math.min(previewFrames, bitmaps.length); i++) wantPreview.add(Math.min(bitmaps.length - 1, Math.round(i * step)));
  }

  for (let i = 0; i < bitmaps.length; i++) {
    const { canvas, blank } = await renderPage({ source: bitmaps[i], fax, machine, replyUrl });
    // The source bitmap is spent the moment its page exists. Holding forty of
    // them is the difference between a phone that finishes and one that
    // hands back blank canvases.
    bitmaps[i].close?.();
    if (blank || !canvas) continue;
    const { indices, width, height } = toIndices(canvas);
    if (!writer) {
      writer = new GifWriter({ width, height });
      // THE POSTER IS A REAL CANVAS, COPIED NOW. An OffscreenCanvas kept alive
      // across every later render is exactly the kind of allocation iOS
      // reclaims under pressure — and it reclaims it by blanking it, silently,
      // which is what "blank when sending" looked like on a phone.
      poster = document.createElement('canvas');
      poster.width = width; poster.height = height;
      poster.getContext('2d').drawImage(canvas, 0, 0);
    } else if (width !== writer.width || height !== writer.height) {
      continue;   // a source whose frames differ in size cannot be one animation
    }
    writer.addFrame(indices, delaysMs?.[i] ?? 110);
    if (wantPreview.has(i)) {
      const pw = Math.min(previewWidth, width);
      previews.push({
        bitmap: await createImageBitmap(canvas, { resizeWidth: pw, resizeHeight: Math.round(height * pw / width), resizeQuality: 'medium' }),
        delayMs: delaysMs?.[i] ?? 110,
      });
    }
    onProgress?.((i + 1) / bitmaps.length);
  }

  if (!writer) throw Object.assign(new Error('Nothing came out of the machine.'), { code: 'blank' });
  return { blob: writer.finish(), poster, frames: writer.n, previews };
}
